import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { color, font, layout, mixin, space } from '../admin_flat_theme';

export const TRAINING_FEE = 110000;

export function calculateTrainingPrice(discountRate) {
    const rate = Number(discountRate);
    if (!Number.isInteger(rate) || rate < 0 || rate > 99) return TRAINING_FEE;
    return Math.floor(TRAINING_FEE * (100 - rate) / 100);
}

const formatPrice = (value) => `${Number(value || 0).toLocaleString('ko-KR')}원`;

function Training() {
    const [form, setForm] = useState({
        training_discount_rate: 0,
        show_training_discount_popup: false
    });
    const [savedSettings, setSavedSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const loadSettings = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/TrainingSettings');
            const settings = response.data.message;
            const normalized = {
                training_discount_rate: Number(settings.training_discount_rate),
                show_training_discount_popup: Boolean(settings.show_training_discount_popup),
                updated_by: settings.updated_by || null,
                updated_at: settings.updated_at || null
            };
            setForm(normalized);
            setSavedSettings(normalized);
        } catch (loadError) {
            console.error('교육 설정 조회 오류:', loadError);
            setError(loadError.response?.data?.message || '교육 설정을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const rate = Number(form.training_discount_rate);
    const isRateValid = Number.isInteger(rate) && rate >= 0 && rate <= 99;
    const discountedPrice = useMemo(
        () => calculateTrainingPrice(form.training_discount_rate),
        [form.training_discount_rate]
    );
    const savedAmount = TRAINING_FEE - discountedPrice;
    const isDirty = savedSettings && (
        rate !== savedSettings.training_discount_rate ||
        form.show_training_discount_popup !== savedSettings.show_training_discount_popup
    );

    const handleRateChange = (event) => {
        const rawValue = event.target.value;
        setError('');
        setNotice('');
        setForm((current) => ({
            ...current,
            training_discount_rate: rawValue === '' ? '' : Number(rawValue)
        }));
    };

    const handlePopupChange = () => {
        setError('');
        setNotice('');
        setForm((current) => ({
            ...current,
            show_training_discount_popup: !current.show_training_discount_popup
        }));
    };

    const saveSettings = async () => {
        if (!isRateValid) {
            setError('할인율은 0~99 사이의 정수로 입력해 주세요.');
            return;
        }

        const confirmed = window.confirm(
            `교육비 설정을 저장하시겠습니까?\n신규 결제 금액이 ${formatPrice(discountedPrice)}으로 적용됩니다.`
        );
        if (!confirmed) return;

        setSaving(true);
        setError('');
        setNotice('');
        try {
            const response = await axios.put('/TrainingSettings', {
                training_discount_rate: rate,
                show_training_discount_popup: form.show_training_discount_popup
            });
            const saved = {
                ...response.data.message,
                updated_at: new Date().toISOString()
            };
            setForm(saved);
            setSavedSettings(saved);
            setNotice('교육비 설정을 저장했습니다.');
        } catch (saveError) {
            console.error('교육 설정 저장 오류:', saveError);
            setError(saveError.response?.data?.message || '교육 설정을 저장하지 못했습니다.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <PageShell>
                <LoadingPanel>교육 설정을 불러오는 중입니다.</LoadingPanel>
            </PageShell>
        );
    }

    if (!savedSettings && error) {
        return (
            <PageShell>
                <ErrorPanel>
                    <strong>설정을 불러오지 못했습니다.</strong>
                    <span>{error}</span>
                    <SecondaryButton type="button" onClick={loadSettings}>다시 시도</SecondaryButton>
                </ErrorPanel>
            </PageShell>
        );
    }

    return (
        <PageShell>
            <PageHeader>
                <div>
                    <Eyebrow>운영관리</Eyebrow>
                    <PageTitle>교육관리</PageTitle>
                    <PageDescription>
                        매니저 교육비 할인 정책과 결제 화면의 할인 안내 노출을 관리합니다.
                    </PageDescription>
                </div>
                <HeaderMeta>
                    <span>교육비 정가</span>
                    <strong>{formatPrice(TRAINING_FEE)}</strong>
                </HeaderMeta>
            </PageHeader>

            {(error || notice) && (
                <StatusMessage $error={Boolean(error)} role={error ? 'alert' : 'status'}>
                    {error || notice}
                </StatusMessage>
            )}

            <ContentGrid>
                <SettingsCard>
                    <SectionHeader>
                        <div>
                            <SectionTitle>결제 정책</SectionTitle>
                            <SectionDescription>저장한 값은 앱 표시와 서버 영수증 검증에 함께 적용됩니다.</SectionDescription>
                        </div>
                        <LiveBadge>전역 설정</LiveBadge>
                    </SectionHeader>

                    <FieldGroup>
                        <FieldLabel htmlFor="training-discount-rate">교육비 할인율</FieldLabel>
                        <RateInputRow>
                            <RateInput
                                id="training-discount-rate"
                                type="number"
                                min="0"
                                max="99"
                                step="1"
                                value={form.training_discount_rate}
                                onChange={handleRateChange}
                                aria-invalid={!isRateValid}
                            />
                            <RateSuffix>%</RateSuffix>
                        </RateInputRow>
                        <FieldHint $error={!isRateValid}>
                            {isRateValid ? '0%는 할인 없음, 최대 99%까지 설정할 수 있습니다.' : '0~99 사이의 정수를 입력해 주세요.'}
                        </FieldHint>
                    </FieldGroup>

                    <Divider />

                    <SwitchRow>
                        <div>
                            <FieldLabel as="span">할인 안내 팝업 노출</FieldLabel>
                            <FieldHint>결제 화면 진입 시 「{isRateValid ? rate : 0}% 할인」 안내를 표시합니다.</FieldHint>
                        </div>
                        <SwitchButton
                            type="button"
                            role="switch"
                            aria-checked={form.show_training_discount_popup}
                            aria-label="할인 안내 팝업 노출"
                            $checked={form.show_training_discount_popup}
                            onClick={handlePopupChange}
                        >
                            <SwitchKnob $checked={form.show_training_discount_popup} />
                        </SwitchButton>
                    </SwitchRow>

                    <SummaryBox>
                        <SummaryItem>
                            <span>정가</span>
                            <strong>{formatPrice(TRAINING_FEE)}</strong>
                        </SummaryItem>
                        <SummaryOperator>−</SummaryOperator>
                        <SummaryItem>
                            <span>할인 금액</span>
                            <strong>{formatPrice(savedAmount)}</strong>
                        </SummaryItem>
                        <SummaryOperator>=</SummaryOperator>
                        <SummaryItem $primary>
                            <span>최종 결제 금액</span>
                            <strong>{formatPrice(discountedPrice)}</strong>
                        </SummaryItem>
                    </SummaryBox>

                    <CardFooter>
                        <UpdatedInfo>
                            {savedSettings?.updated_at
                                ? `최근 저장 ${new Date(savedSettings.updated_at).toLocaleString('ko-KR')}${savedSettings.updated_by ? ` · ${savedSettings.updated_by}` : ''}`
                                : '아직 변경된 이력이 없습니다.'}
                        </UpdatedInfo>
                        <PrimaryButton
                            type="button"
                            onClick={saveSettings}
                            disabled={!isDirty || !isRateValid || saving}
                        >
                            {saving ? '저장 중…' : '설정 저장'}
                        </PrimaryButton>
                    </CardFooter>
                </SettingsCard>

                <PreviewCard>
                    <SectionHeader>
                        <div>
                            <SectionTitle>팝업 문구 미리보기</SectionTitle>
                            <SectionDescription>매니저 앱에서 보게 될 할인 안내 내용입니다.</SectionDescription>
                        </div>
                        <VisibilityState $visible={form.show_training_discount_popup}>
                            {form.show_training_discount_popup ? '노출' : '비노출'}
                        </VisibilityState>
                    </SectionHeader>

                    <PhonePreview $disabled={!form.show_training_discount_popup}>
                        <PreviewBackdrop aria-hidden="true">
                            <i />
                            <i />
                            <i />
                            <i />
                        </PreviewBackdrop>
                        <PopupPreview>
                            <DiscountLabel>{isRateValid ? rate : 0}% 할인</DiscountLabel>
                            <PopupTitle>교육비 할인 혜택이 적용됐어요</PopupTitle>
                            <PopupCopy>
                                매니저 교육비를 지금 <strong>{formatPrice(discountedPrice)}</strong>에 결제할 수 있습니다.
                            </PopupCopy>
                            <PriceComparison>
                                <del>{formatPrice(TRAINING_FEE)}</del>
                                <span>{formatPrice(discountedPrice)}</span>
                            </PriceComparison>
                            <PreviewAction>확인</PreviewAction>
                        </PopupPreview>
                        {!form.show_training_discount_popup && (
                            <DisabledOverlay>
                                <strong>팝업 비노출</strong>
                                <span>할인 금액은 그대로 적용됩니다.</span>
                            </DisabledOverlay>
                        )}
                    </PhonePreview>

                    <PreviewNote>
                        팝업을 끄더라도 할인율과 최종 결제 금액은 변경되지 않습니다.
                    </PreviewNote>
                </PreviewCard>
            </ContentGrid>
        </PageShell>
    );
}

const PageShell = styled.main`
    min-height: 100%;
    padding: ${layout.pagePadding};
    background: ${color.surface};
    color: ${color.text};

    @media (max-width: 720px) {
        padding: ${layout.pagePaddingSmall};
    }
`;

const PageHeader = styled.header`
    max-width: ${layout.maxWidth};
    margin: 0 auto ${space.xl};
    padding-bottom: ${space.xl};
    border-bottom: 1px solid ${color.line};
    display: flex;
    justify-content: space-between;
    gap: ${space.xxl};
    align-items: flex-end;

    @media (max-width: 720px) {
        align-items: flex-start;
        flex-direction: column;
        gap: ${space.lg};
    }
`;

const Eyebrow = styled.div`
    margin-bottom: ${space.sm};
    color: ${color.textFaint};
    ${font.eyebrow};
`;

const PageTitle = styled.h1`
    margin: 0;
    color: ${color.textStrong};
    ${font.pageTitle};
`;

const PageDescription = styled.p`
    max-width: 620px;
    margin: ${space.md} 0 0;
    color: ${color.textMuted};
    ${font.body};
`;

const HeaderMeta = styled.div`
    flex: 0 0 auto;
    min-width: 180px;
    padding-left: ${space.xl};
    border-left: 1px solid ${color.line};
    display: flex;
    flex-direction: column;
    gap: ${space.xs};

    span {
        color: ${color.textFaint};
        ${font.eyebrow};
    }

    strong {
        color: ${color.textStrong};
        font-size: 22px;
        font-weight: 700;
        letter-spacing: -0.02em;
    }

    @media (max-width: 720px) {
        padding-left: 0;
        border-left: 0;
    }
`;

const StatusMessage = styled.div`
    max-width: ${layout.maxWidth};
    margin: 0 auto ${space.lg};
    padding: ${space.md} ${space.lg};
    border: 1px solid ${props => props.$error ? color.danger : color.line};
    border-left-width: 3px;
    background: ${color.surface};
    color: ${props => props.$error ? color.danger : color.text};
    ${font.caption};
    font-weight: 600;
`;

const ContentGrid = styled.div`
    max-width: ${layout.maxWidth};
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(330px, 0.85fr);
    gap: ${space.xl};
    align-items: start;

    @media (max-width: 920px) {
        grid-template-columns: 1fr;
    }
`;

const BaseCard = styled.section`
    ${mixin.panel};
    min-width: 0;
`;

const SettingsCard = styled(BaseCard)`
    padding: ${space.xl};

    @media (max-width: 720px) {
        padding: ${space.lg};
    }
`;

const PreviewCard = styled(BaseCard)`
    padding: ${space.xl};

    @media (max-width: 720px) {
        padding: ${space.lg};
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${space.lg};
    margin-bottom: ${space.xl};
`;

const SectionTitle = styled.h2`
    margin: 0;
    color: ${color.textStrong};
    ${font.sectionTitle};
`;

const SectionDescription = styled.p`
    margin: 6px 0 0;
    color: ${color.textMuted};
    ${font.caption};
`;

const LiveBadge = styled.span`
    ${mixin.statusChip};
    color: ${color.textMuted};
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const FieldLabel = styled.label`
    color: ${color.textStrong};
    ${font.label};
`;

const RateInputRow = styled.div`
    position: relative;
    width: min(220px, 100%);
    margin-top: ${space.sm};
`;

const RateInput = styled.input`
    ${mixin.field};
    height: 52px;
    padding: 0 46px 0 14px;
    color: ${color.textStrong};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    border-color: ${props => props['aria-invalid'] ? color.danger : color.lineStrong};
`;

const RateSuffix = styled.span`
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    color: ${color.textFaint};
    font-size: 15px;
    font-weight: 700;
    pointer-events: none;
`;

const FieldHint = styled.span`
    display: block;
    margin-top: ${space.sm};
    color: ${props => props.$error ? color.danger : color.textFaint};
    ${font.caption};
`;

const Divider = styled.hr`
    margin: ${space.xl} 0;
    border: 0;
    ${mixin.hairlineTop};
`;

const SwitchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space.xl};
`;

const SwitchButton = styled.button`
    ${mixin.switchTrack};
    background: ${props => props.$checked ? color.accent : color.surfaceMuted};
    border-color: ${props => props.$checked ? color.accent : color.lineStrong};

    &:focus-visible {
        outline: 2px solid ${color.accent};
        outline-offset: 2px;
    }
`;

const SwitchKnob = styled.span`
    ${mixin.switchKnob};
    left: ${props => props.$checked ? '23px' : '3px'};
    background: ${props => props.$checked ? color.onAccent : color.lineStrong};
`;

const SummaryBox = styled.div`
    margin-top: ${space.xl};
    padding: ${space.lg};
    border: 1px solid ${color.line};
    background: ${color.surfaceMuted};
    display: grid;
    grid-template-columns: 1fr auto 1fr auto 1.15fr;
    gap: ${space.md};
    align-items: center;

    @media (max-width: 620px) {
        grid-template-columns: 1fr;
    }
`;

const SummaryItem = styled.div`
    min-width: 0;
    color: ${props => props.$primary ? color.accent : color.textStrong};

    span {
        display: block;
        margin-bottom: 5px;
        color: ${color.textFaint};
        ${font.eyebrow};
    }

    strong {
        font-size: ${props => props.$primary ? '20px' : '15px'};
        font-weight: 700;
        letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }
`;

const SummaryOperator = styled.span`
    color: ${color.textFaint};
    font-size: 15px;

    @media (max-width: 620px) {
        display: none;
    }
`;

const CardFooter = styled.div`
    margin-top: ${space.xl};
    padding-top: ${space.xl};
    ${mixin.hairlineTop};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space.lg};

    @media (max-width: 620px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

const UpdatedInfo = styled.span`
    color: ${color.textFaint};
    ${font.caption};
`;

const PrimaryButton = styled.button`
    ${mixin.buttonBase};
    ${mixin.buttonPrimary};
    min-width: 116px;
`;

const SecondaryButton = styled(PrimaryButton)`
    ${mixin.buttonGhost};
    margin-top: ${space.md};
`;

const VisibilityState = styled.span`
    ${mixin.statusChip};
    color: ${props => props.$visible ? color.success : color.textMuted};
    border-color: ${props => props.$visible ? color.success : color.line};
`;

const PhonePreview = styled.div`
    position: relative;
    min-height: 380px;
    padding: 52px ${space.xl} ${space.xxl};
    overflow: hidden;
    border: 1px solid ${color.line};
    background: ${color.surfaceMuted};
`;

const PreviewBackdrop = styled.div`
    position: absolute;
    inset: 0;
    padding: ${space.xl};
    display: flex;
    flex-direction: column;
    gap: ${space.md};

    i {
        display: block;
        background: ${color.surface};
    }

    i:nth-child(1) { height: 44px; }
    i:nth-child(2) { width: 52%; height: 10px; }
    i:nth-child(3) { width: 72%; height: 10px; }
    i:nth-child(4) { flex: 1; }
`;

const PopupPreview = styled.div`
    position: relative;
    z-index: 1;
    margin-top: ${space.xxl};
    padding: ${space.xl};
    border: 1px solid ${color.lineStrong};
    background: ${color.surface};
    text-align: center;
`;

const DiscountLabel = styled.div`
    display: inline-block;
    margin-bottom: ${space.md};
    padding: 4px 8px;
    border: 1px solid ${color.accent};
    color: ${color.accent};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
`;

const PopupTitle = styled.h3`
    margin: 0;
    color: ${color.textStrong};
    ${font.sectionTitle};
`;

const PopupCopy = styled.p`
    margin: ${space.sm} auto ${space.lg};
    color: ${color.textMuted};
    ${font.caption};

    strong {
        color: ${color.textStrong};
        font-weight: 700;
    }
`;

const PriceComparison = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: ${space.sm};
    margin-bottom: ${space.lg};

    del {
        color: ${color.textFaint};
        font-size: 12px;
    }

    span {
        color: ${color.accent};
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
    }
`;

const PreviewAction = styled.div`
    padding: 11px;
    border: 1px solid ${color.accent};
    background: ${color.accent};
    color: ${color.onAccent};
    font-size: 12px;
    font-weight: 700;
`;

const DisabledOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: ${color.scrim};
    color: ${color.textMuted};

    strong {
        color: ${color.textStrong};
        ${font.sectionTitle};
    }

    span {
        ${font.caption};
    }
`;

const PreviewNote = styled.p`
    margin: ${space.md} 0 0;
    color: ${color.textFaint};
    ${font.caption};
`;

const LoadingPanel = styled.div`
    ${mixin.panel};
    max-width: ${layout.maxWidth};
    margin: ${space.xxxl} auto;
    padding: ${space.xxxl};
    color: ${color.textMuted};
    ${font.body};
    text-align: center;
`;

const ErrorPanel = styled(LoadingPanel)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: ${color.textMuted};

    strong {
        color: ${color.danger};
        ${font.sectionTitle};
    }
`;

export default Training;
